import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { t } from '../utils/i18n.js';
import { hrefSeguro } from '../utils/links.js';
import { getWaFlotante } from '../utils/cta-destino.js';
import { escapeHtml } from '../utils/dom.js';

    export const footerBlock = (rawProps) => {
        const p = safe(rawProps);
        const variant = p.variant || 'v1';

        const nombreMarca = p.brand || p.logo || 'Mi Sitio Web';

        // --- WHATSAPP ---
        // Estos data-* NO pintan nada: son de donde se alimenta el botón flotante
        // (animations/whatsapp.js). Si el sitio tiene destino global de tipo WhatsApp, ese manda —
        // un solo número para botones y flotante— y `p.whatsapp` queda como respaldo de las webs
        // creadas antes del módulo. Con el flotante apagado se emiten vacíos y no aparece icono.
        const waGlobal = getWaFlotante();
        const waNum = waGlobal.oculto ? '' : ((waGlobal.num || p.whatsapp || '').replace(/\D/g, ''));
        const waMsg = encodeURIComponent(waGlobal.msg || p.whatsapp_msg || `${t('wa_prefill')} ${nombreMarca}`);

        // --- CLICK-TO-CALL ---
        const phoneNum = p.phone || '';
        const phoneSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.89a16 16 0 0 0 6.18 6.18l1.78-1.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`;
        const phoneBtn = phoneNum ? `
      <a href="tel:${phoneNum}" data-field="phone" class="footer-phone-link"
         style="display:inline-flex; align-items:center; gap:8px; margin-top:20px; opacity:0.7; font-weight:600; font-size:calc(1rem * var(--text-scale, 1)); text-decoration:none; transition:0.3s;">
        ${phoneSvg} ${phoneNum}
      </a>` : '';

        const copyright = p.text || `© ${new Date().getFullYear()}. ${t('rights')}`;

        // --- ENLACES DEL PIE (letra pequeña) ---
        // Dos clases, en este orden (utils/pie-enlaces.js): los PROPIOS del cliente (una sola
        // lista por idioma, la misma en todas sus páginas) y los LEGALES, uno por página
        // `nav:false` (aviso legal / privacidad / cookies), que NO van al menú del header. El
        // contrato es el MISMO que el del header (texto contenteditable + URL en data-link) →
        // extractor.js los lee con el handler `footer`. Nunca hay texto hardcodeado: si no hay
        // links, no hay nav.
        //
        // La X para quitar uno NO se pinta aquí: la pone editor/tools.js sobre el DOM ya pintado,
        // porque compileSiteHtml llama a este render con editorMode encendido y cualquier envoltorio
        // acabaría en el HTML publicado. Quitar un legal lo saca de todos los pies (su página queda
        // `pie:false`) sin borrar la página.
        // OJO: el "¿hay enlaces?" se decide sobre rawProps, NO sobre p: safe() inyecta un
        // [{text:"Inicio", url:"#"}] por defecto (contrast.js) pensado para que el HEADER nunca
        // salga sin menú. En el pie ese default pintaría un "Inicio" fantasma en TODOS los sitios
        // que no tienen enlaces legales. Cuando sí hay, se usa `p.links` para reaprovechar la
        // normalización de safe() (admite items en texto plano: 'Aviso' → {text:'Aviso', url:'#'}).
        // Escape del contenido de enlaces: `text` es editable por el cliente y `url` puede venir
        // de una edición manual → sin escapar romperían el atributo o inyectarían markup (en el
        // editor no hay sanitizer). `href` solo admite esquemas seguros o rutas relativas/ancla.
        // El saneo de href (hrefSeguro) y el escape (escapeHtml) son SSOT compartidas (utils/
        // links.js + utils/dom.js) — la MISMA lógica que usan los CTA de los bloques (DRY).
        const links = Array.isArray(rawProps.links) ? p.links : [];
        const linksHtml = links.length ? `
      <nav class="footer-links">
        ${links.map((l, i) => `
        <a href="${hrefSeguro(l.url)}" data-link="${hrefSeguro(l.url)}" contenteditable="true"
           class="editable-link footer-link" style="--link-i: ${i};">${escapeHtml(l.text)}</a>`).join('')}
      </nav>` : '';

        // --- v1 (actual): marca centrada + copyright ---
        if (variant === 'v1' || (!variant || variant === '')) {
            return `
        <footer data-wa-num="${waNum}" data-wa-msg="${waMsg}" class="footer-layout"
                style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
          <h2 data-field="brand" contenteditable="true" style="font-size:2rem; margin-bottom:15px; font-weight: 800;">${nombreMarca}</h2>
          <p data-field="text" contenteditable="true" style="opacity:0.5; font-size:calc(0.9rem * var(--text-scale, 1));">${copyright}</p>
          ${phoneBtn}
          ${linksHtml}
        </footer>`;
        }

        // --- v2 ("minimal"): una fila horizontal — logo | copyright | teléfono ---
        if (variant === 'v2') {
            return `
        <footer data-wa-num="${waNum}" data-wa-msg="${waMsg}" class="footer-minimal"
                style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
          <div class="footer-minimal-container">
            <h2 data-field="brand" contenteditable="true" style="font-size:1.4rem; font-weight: 800; margin: 0;">${nombreMarca}</h2>
            <p data-field="text" contenteditable="true" style="opacity:0.5; font-size:calc(0.85rem * var(--text-scale, 1)); margin: 0;">${copyright}</p>
            ${phoneBtn ? `<div style="margin-top: 0;">${phoneBtn.replace('margin-top:20px;', 'margin-top:0;')}</div>` : ''}
            ${linksHtml}
          </div>
        </footer>`;
        }

        // --- v3 ("stacked"): logo grande centrado + separador + copyright abajo ---
        return `
        <footer data-wa-num="${waNum}" data-wa-msg="${waMsg}" class="footer-layout"
                style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
          <h2 data-field="brand" contenteditable="true" style="font-size: 2.5rem; font-weight: 900; margin-bottom: 20px; letter-spacing: -0.5px;">${nombreMarca}</h2>
          ${phoneBtn}
          <div style="width: 60px; height: 2px; background: var(--accent); margin: 30px auto; opacity: 0.6;"></div>
          <p data-field="text" contenteditable="true" style="opacity:0.4; font-size:calc(0.85rem * var(--text-scale, 1));">${copyright}</p>
          ${linksHtml}
        </footer>`;
    }
